import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Calendar, PlayCircle, StopCircle, Pencil, Copy,
  RotateCcw, Trash2, Trophy, Gamepad2, Users, Clock, Twitter, Bell, Send, Save, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ChallengeBuilder from './ChallengeBuilder';
import ChallengeEditModal from './ChallengeEditModal';
import ChallengeShareCard from './ChallengeShareCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to convert local datetime to ISO
const localDatetimeToIso = (localDatetime) => {
  if (!localDatetime) return null;
  try {
    const date = new Date(localDatetime);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch (e) {
    return null;
  }
};

export const ChallengesTab = ({
  challenges = [],
  token,
  onRefresh
}) => {
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [reusingChallenge, setReusingChallenge] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [sharingChallenge, setSharingChallenge] = useState(null);
  const [pushingNotification, setPushingNotification] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [loadedDraft, setLoadedDraft] = useState(null);

  // Fetch drafts on mount
  React.useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const response = await axios.get(`${API}/admin/challenges/drafts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrafts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch drafts:', error);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleLoadDraft = (draft) => {
    setLoadedDraft(draft);
    setShowBuilder(true);
    setReusingChallenge(null);
  };

  const handlePublishDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to publish this draft? It will become a scheduled challenge.')) return;

    try {
      await axios.post(`${API}/admin/challenges/${draftId}/publish-draft`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Draft published as scheduled challenge!');
      fetchDrafts();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish draft');
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;

    try {
      await axios.delete(`${API}/admin/challenges/${draftId}/draft`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Draft deleted');
      fetchDrafts();
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };

  const handlePushNotification = async (challengeId, discord = true, email = true) => {
    setPushingNotification(challengeId);
    try {
      const response = await axios.post(`${API}/admin/challenges/${challengeId}/push-notification`,
        { notify_discord: discord, notify_email: email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push notification');
    } finally {
      setPushingNotification(null);
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!window.confirm('Are you sure you want to delete this challenge? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/admin/challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Challenge deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete challenge');
    }
  };

  const handleActivateChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/admin/challenges/${challengeId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Challenge activated!');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate challenge');
    }
  };

  const handleReactivateChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/admin/challenges/${challengeId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Challenge reactivated!');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate challenge');
    }
  };

  const handleEndChallenge = async (challengeId) => {
    if (!window.confirm('Are you sure you want to end this challenge?')) return;

    try {
      await axios.post(`${API}/admin/challenges/${challengeId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Challenge ended');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end challenge');
    }
  };

  const handleReuseChallenge = (challenge) => {
    setReusingChallenge({
      ...challenge,
      title: `${challenge.title} (Copy)`,
      status: 'scheduled',
      start_time: null,
      end_time: null
    });
    setLoadedDraft(null);
    setShowBuilder(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      scheduled: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      ended: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return styles[status] || styles.scheduled;
  };

  const safeChallenges = Array.isArray(challenges) ? challenges : [];
  const safeDrafts = Array.isArray(drafts) ? drafts : [];

  const activeChallenges = safeChallenges.filter(c => c.status === 'active');
  const scheduledChallenges = safeChallenges.filter(c => c.status === 'scheduled');
  const endedChallenges = safeChallenges.filter(c => c.status === 'ended');

  return (
    <div className="space-y-6">
      {/* Challenge Builder Toggle */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-white">Manage Challenges</h3>
        <Button
          onClick={() => {
            setReusingChallenge(null);
            setLoadedDraft(null);
            setShowBuilder(!showBuilder);
          }}
          className="bg-red-600 hover:bg-red-700"
        >
          {showBuilder ? 'Hide Builder' : '+ New Challenge'}
        </Button>
      </div>

      {/* Challenge Builder */}
      {showBuilder && (
        <ChallengeBuilder
          token={token}
          API={API}
          challenges={safeChallenges}
          refreshData={() => {
            onRefresh();
            fetchDrafts();
          }}
          localDatetimeToIso={localDatetimeToIso}
          initialData={reusingChallenge}
          loadedDraft={loadedDraft}
        />
      )}

      {/* Drafts Section */}
      {safeDrafts.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
            <Save className="w-4 h-4" />
            Drafts ({safeDrafts.length})
          </h4>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {safeDrafts.map(draft => (
              <DraftCard
                key={draft.id}
                draft={draft}
                onLoad={() => handleLoadDraft(draft)}
                onPublish={() => handlePublishDraft(draft.id)}
                onDelete={() => handleDeleteDraft(draft.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Active Challenges */}
      {activeChallenges.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
            <PlayCircle className="w-4 h-4" />
            Active Challenges ({activeChallenges.length})
          </h4>
          <div className="grid gap-4 md:grid-cols-2">
            {activeChallenges.map(challenge => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onEdit={() => setEditingChallenge(challenge)}
                onEnd={() => handleEndChallenge(challenge.id)}
                onDelete={() => handleDeleteChallenge(challenge.id)}
                onReuse={() => handleReuseChallenge(challenge)}
                onShare={() => setSharingChallenge(challenge)}
                onPushNotification={(discord, email) => handlePushNotification(challenge.id, discord, email)}
                isPushingNotification={pushingNotification === challenge.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Scheduled Challenges */}
      {scheduledChallenges.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-yellow-400 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Scheduled Challenges ({scheduledChallenges.length})
          </h4>
          <div className="grid gap-4 md:grid-cols-2">
            {scheduledChallenges.map(challenge => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onEdit={() => setEditingChallenge(challenge)}
                onActivate={() => handleActivateChallenge(challenge.id)}
                onDelete={() => handleDeleteChallenge(challenge.id)}
                onReuse={() => handleReuseChallenge(challenge)}
                onShare={() => setSharingChallenge(challenge)}
                onPushNotification={(discord, email) => handlePushNotification(challenge.id, discord, email)}
                isPushingNotification={pushingNotification === challenge.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Ended Challenges */}
      {endedChallenges.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <StopCircle className="w-4 h-4" />
            Ended Challenges ({endedChallenges.length})
          </h4>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {endedChallenges.slice(0, 6).map(challenge => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onEdit={() => setEditingChallenge(challenge)}
                onReactivate={() => handleReactivateChallenge(challenge.id)}
                onDelete={() => handleDeleteChallenge(challenge.id)}
                onReuse={() => handleReuseChallenge(challenge)}
                compact
              />
            ))}
          </div>
        </div>
      )}

      {challenges.length === 0 && (
        <Card className="glass-card p-8 text-center">
          <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No challenges yet. Create your first one!</p>
        </Card>
      )}

      {/* Edit Modal */}
      {editingChallenge && (
        <ChallengeEditModal
          challenge={editingChallenge}
          token={token}
          onClose={() => setEditingChallenge(null)}
          onSuccess={() => { setEditingChallenge(null); onRefresh(); }}
        />
      )}

      {/* Share Card Modal */}
      {sharingChallenge && (
        <ChallengeShareCard
          challenge={sharingChallenge}
          onClose={() => setSharingChallenge(null)}
        />
      )}
    </div>
  );
};

// Challenge Card Component
const ChallengeCard = ({
  challenge,
  onEdit,
  onActivate,
  onReactivate,
  onEnd,
  onDelete,
  onReuse,
  onShare,
  onPushNotification,
  isPushingNotification,
  compact = false
}) => {
  const [showPushOptions, setShowPushOptions] = useState(false);

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400',
      scheduled: 'bg-yellow-500/20 text-yellow-400',
      ended: 'bg-gray-500/20 text-gray-400'
    };
    return styles[status] || styles.scheduled;
  };

  return (
    <Card className="glass-card">
      <CardContent className={compact ? "p-3" : "p-4"}>
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(challenge.status)}`}>
                {challenge.status.toUpperCase()}
              </span>
              <span className="text-yellow-500 font-bold text-sm">${challenge.prize_amount}</span>
            </div>
            <h4 className="font-bold text-white truncate">{challenge.title}</h4>
          </div>
          {challenge.game_cover_url && (
            <img
              src={challenge.game_cover_url}
              alt={challenge.game_name}
              className="w-12 h-16 object-cover rounded ml-3"
            />
          )}
        </div>

        {/* Game & Stats */}
        <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
          {challenge.game_name && (
            <span className="flex items-center gap-1">
              <Gamepad2 className="w-3 h-3" />
              {challenge.game_name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            {challenge.submission_count || 0} submissions
          </span>
          <span className="flex items-center gap-1">
            <Trophy className="w-3 h-3" />
            {challenge.winners_picked || 0}/{challenge.max_winners || 1} winners
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={onEdit} className="text-xs">
            <Pencil className="w-3 h-3 mr-1" /> Edit
          </Button>

          {challenge.status === 'scheduled' && onActivate && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs" onClick={onActivate}>
              <PlayCircle className="w-3 h-3 mr-1" /> Activate
            </Button>
          )}

          {challenge.status === 'active' && onEnd && (
            <Button size="sm" variant="destructive" onClick={onEnd} className="text-xs">
              <StopCircle className="w-3 h-3 mr-1" /> End
            </Button>
          )}

          {challenge.status === 'ended' && onReactivate && (
            <Button size="sm" variant="outline" onClick={onReactivate} className="text-xs">
              <RotateCcw className="w-3 h-3 mr-1" /> Reactivate
            </Button>
          )}

          {(challenge.status === 'active' || challenge.status === 'scheduled') && onShare && (
            <Button size="sm" variant="outline" onClick={onShare} className="text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/10">
              <Twitter className="w-3 h-3 mr-1" /> Share
            </Button>
          )}

          {/* Push Notification Button - Only for active/scheduled */}
          {(challenge.status === 'active' || challenge.status === 'scheduled') && onPushNotification && (
            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPushOptions(!showPushOptions)}
                disabled={isPushingNotification}
                className="text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              >
                {isPushingNotification ? (
                  <div className="w-3 h-3 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin mr-1" />
                ) : (
                  <Bell className="w-3 h-3 mr-1" />
                )}
                Push
              </Button>

              {showPushOptions && !isPushingNotification && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowPushOptions(false)}
                  />
                  {/* Dropdown menu */}
                  <div className="absolute bottom-full left-0 mb-1 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50 min-w-[160px] overflow-hidden">
                    <button
                      onClick={() => { onPushNotification(true, true); setShowPushOptions(false); }}
                      className="w-full px-3 py-2.5 text-xs text-left text-white hover:bg-gray-800 flex items-center gap-2 border-b border-gray-800"
                    >
                      <Send className="w-3 h-3 text-green-400" />
                      Discord + Email
                    </button>
                    <button
                      onClick={() => { onPushNotification(true, false); setShowPushOptions(false); }}
                      className="w-full px-3 py-2.5 text-xs text-left text-white hover:bg-gray-800 flex items-center gap-2 border-b border-gray-800"
                    >
                      <Bell className="w-3 h-3 text-indigo-400" />
                      Discord Only
                    </button>
                    <button
                      onClick={() => { onPushNotification(false, true); setShowPushOptions(false); }}
                      className="w-full px-3 py-2.5 text-xs text-left text-white hover:bg-gray-800 flex items-center gap-2"
                    >
                      <Send className="w-3 h-3 text-blue-400" />
                      Email Only
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <Button size="sm" variant="outline" onClick={onReuse} className="text-xs">
            <Copy className="w-3 h-3 mr-1" /> Clone
          </Button>

          <Button size="sm" variant="ghost" onClick={onDelete} className="text-xs text-red-400 hover:text-red-300">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Draft Card Component
const DraftCard = ({ draft, onLoad, onPublish, onDelete }) => {
  return (
    <Card className="glass-card border-purple-500/30">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
                DRAFT
              </span>
            </div>
            <h4 className="font-bold text-white truncate">{draft.title || 'Untitled Draft'}</h4>
          </div>
          {draft.game_cover_url && (
            <img
              src={draft.game_cover_url}
              alt={draft.game_name}
              className="w-12 h-16 object-cover rounded ml-3"
            />
          )}
        </div>

        {/* Game & Info */}
        <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
          {draft.game_name && (
            <span className="flex items-center gap-1">
              <Gamepad2 className="w-3 h-3" />
              {draft.game_name}
            </span>
          )}
          <span className="flex items-center gap-1 text-yellow-400">
            <Trophy className="w-3 h-3" />
            ${draft.prize_amount}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(draft.created_at).toLocaleDateString()}
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={onLoad} className="text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
            <FileText className="w-3 h-3 mr-1" /> Continue Editing
          </Button>

          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs" onClick={onPublish}>
            <PlayCircle className="w-3 h-3 mr-1" /> Publish
          </Button>

          <Button size="sm" variant="ghost" onClick={onDelete} className="text-xs text-red-400 hover:text-red-300">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChallengesTab;
